import { useInfiniteQuery } from '@tanstack/react-query'

import {
  EContactSortBy,
  EContactSortDirection,
  TCampaignGetContactPayload,
} from '~/features/campaigns/models'
import { queryKeys } from '~/features/campaigns/utils'
import { EHttpService, EServerPath } from '~/models/api'
import HttpService from '~/services/http'
import { HttpServiceBaseResponse } from '~/services/http/models'
import pathBuilder from '~/utils/api'

const getCampaignContactsPaginated = async ({
  pageParam = 1,
  campaignId,
  limit = 50,
  search,
  outreachFilter,
  contactStatusFilter,
  sourceTypeFilter,
  assistantFilter,
  goalFilter,
  sentimentFilter,
  sourceId,
  sortBy,
  sortDirection,
}: {
  pageParam?: number
  campaignId: string
  limit?: number
  search?: string
  outreachFilter?: string
  contactStatusFilter?: string[]
  sourceTypeFilter?: string[]
  assistantFilter?: string[]
  goalFilter?: string[]
  sentimentFilter?: string[]
  sourceId?: string
  sortBy?: EContactSortBy
  sortDirection?: EContactSortDirection
}): Promise<HttpServiceBaseResponse<TCampaignGetContactPayload>> => {
  return HttpService.get<TCampaignGetContactPayload>(
    {
      path: pathBuilder(EServerPath.campaignContactsPaginated, {
        id: campaignId,
      }),
      query: {
        page: pageParam.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(outreachFilter && { outreachFilter }),
        ...(contactStatusFilter?.length && {
          contactStatusFilter,
        }),
        ...(sourceId && { sourceId }),
        ...(!sourceId &&
          sourceTypeFilter?.length && {
            sourceTypeFilter,
          }),
        ...(assistantFilter?.length && {
          assistantFilter,
        }),
        ...(goalFilter?.length && { goalFilter }),
        ...(sentimentFilter?.length && { sentimentFilter }),
        ...(sortBy && {
          sortBy,
          ...(sortDirection && { sortDirection }),
        }),
      },
    },
    EHttpService.internal,
  )
}

export type TUseGetCampaignContactsInfiniteParams = {
  campaignId: string
  limit?: number
  search?: string
  outreachFilter?: string
  contactStatusFilter?: string[]
  sourceTypeFilter?: string[]
  assistantFilter?: string[]
  goalFilter?: string[]
  sentimentFilter?: string[]
  sourceId?: string
  isDisabled?: boolean
  sortBy?: EContactSortBy
  sortDirection?: EContactSortDirection
}

const useGetCampaignContactsPaginated = ({
  campaignId,
  limit = 50,
  search = '',
  outreachFilter = 'all',
  contactStatusFilter,
  sourceTypeFilter,
  assistantFilter,
  goalFilter,
  sentimentFilter,
  sourceId,
  isDisabled = false,
  sortBy,
  sortDirection,
}: TUseGetCampaignContactsInfiniteParams) => {
  return useInfiniteQuery({
    queryKey: queryKeys.campaignContactsPaginated(
      campaignId,
      limit,
      search,
      outreachFilter,
      contactStatusFilter,
      sourceTypeFilter,
      assistantFilter,
      sourceId,
      sortBy,
      sortDirection,
      goalFilter,
      sentimentFilter,
    ),
    queryFn: ({ pageParam }) =>
      getCampaignContactsPaginated({
        pageParam: pageParam as number,
        campaignId,
        limit,
        search,
        outreachFilter,
        contactStatusFilter,
        sourceTypeFilter: sourceId ? undefined : sourceTypeFilter,
        assistantFilter,
        goalFilter,
        sentimentFilter,
        sourceId,
        sortBy,
        sortDirection,
      }),
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer items than the limit, we've reached the end
      const hasMore =
        lastPage.data && lastPage.data.campaignContacts.length === limit
      return hasMore ? allPages.length + 1 : undefined
    },
    initialPageParam: 1,
    enabled: !!campaignId && !isDisabled,
  })
}

export default useGetCampaignContactsPaginated
